import { useCallback, useEffect, useRef, useState } from 'react';

export function useDebouncedValue<T extends string | number>(value: T, delay: number) {
  const { debounce } = useDebounce<T>(delay);
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    if (value === debouncedValue) {
      return;
    }

    debounce(value).then((debounced) => {
      setDebouncedValue(debounced ?? value);
    });
  }, [value, debounce, debouncedValue]);

  return debouncedValue;
}
export function useDebounce<T extends string | number | null>(delay: number) {
  const timeout = useRef<NodeJS.Timeout>();
  const debouncedValue = useRef<T>();

  useEffect(() => {
    const original = timeout.current;
    return () => clearTimeout(original);
  }, []);

  const debounce = useCallback(
    async function (value: T) {
      clearTimeout(timeout.current);
      if (value === debouncedValue.current) {
        return debouncedValue.current;
      }

      await new Promise<void>((resolve) => {
        timeout.current = setTimeout(() => {
          debouncedValue.current = value;
          resolve();
        }, delay);
      });
      return debouncedValue.current;
    },
    [delay]
  );

  const current = useCallback(() => debouncedValue.current, []);

  return { debounce, current };
}
